import {
  Directive,
  Input,
  TemplateRef,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
  ViewContainerRef,
  ComponentRef
} from '@angular/core';
import {
  OverlayRef,
  Overlay,
  OverlayPositionBuilder,
  ConnectedPosition
} from '@angular/cdk/overlay';
import {
  ComponentType,
  ComponentPortal,
  TemplatePortal
} from '@angular/cdk/portal';
import { Subscription } from 'rxjs';
import { Component, ChangeDetectionStrategy } from '@angular/core';

@Component({
  selector: 'custom-tooltip-string',
  template: `<div class="tooltip-string">{{ content }}</div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class TooltipStringComponent {
  content: string = '';
}

// Default and directional position configuration
const defaultPositions: ConnectedPosition[] = [
  {
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'bottom',
    offsetX: 0,
    offsetY: -15,
    panelClass: 'top',
  },
  {
    originX: 'end',
    originY: 'center',
    overlayX: 'start',
    overlayY: 'center',
    offsetX: 15,
    offsetY: 0,
    panelClass: 'right',
  },
  {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetX: 0,
    offsetY: 15,
    panelClass: 'bottom',
  },
  {
    originX: 'start',
    originY: 'center',
    overlayX: 'end',
    overlayY: 'center',
    offsetX: -15,
    offsetY: 0,
    panelClass: 'left',
  },
];

export const positionPresets: Record<string, ConnectedPosition[]> = {
  top: [defaultPositions[0], ...defaultPositions.slice(1)],
  right: [defaultPositions[1], ...defaultPositions.filter((_, i) => i !== 1)],
  bottom: [defaultPositions[2], ...defaultPositions.filter((_, i) => i !== 2)],
  left: [defaultPositions[3], ...defaultPositions.filter((_, i) => i !== 3)],
};

@Directive({
  selector: '[customTooltip]',
  standalone: true,
})
export class CustomTooltipDirective implements OnInit, OnDestroy {
  @Input('customTooltip') tooltipContent: TemplateRef<any> | ComponentType<any> | string | null = null;
  @Input('customTooltipType') customTooltipType: 'default' = 'default';
  @Input('prefPos') prefPos: 'top' | 'right' | 'bottom' | 'left' = 'top';

  private overlayRef?: OverlayRef;
  private positionSub?: Subscription;
  private hoverState = false;
  private detachTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    private overlay: Overlay,
    private positionBuilder: OverlayPositionBuilder,
    private elRef: ElementRef,
    private vcr: ViewContainerRef
  ) { }

  ngOnInit(): void {
    // Reserved for future initialization logic
  }

  ngOnDestroy(): void {
    this.detach();
    this.positionSub?.unsubscribe();
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (!this.tooltipContent) return;

    this.hoverState = true;
    clearTimeout(this.detachTimeout);

    if (!this.overlayRef) {
      this.createOverlay();
    }

    if (!this.overlayRef!.hasAttached()) {
      this.attachContent();
    }

    this.listenToTooltipHover();
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.hoverState = false;
    clearTimeout(this.detachTimeout);
    this.detachTimeout = setTimeout(() => {
      if (!this.hoverState && this.overlayRef?.hasAttached()) {
        this.overlayRef.detach();
      }
    }, 200);
  }

  private createOverlay(): void {
    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.elRef)
      .withPositions(positionPresets[this.prefPos])
      .withPush(false);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.close(),
      panelClass: ['custom-tooltip', this.customTooltipType],
      hasBackdrop: false,
    });

    this.positionSub = positionStrategy.positionChanges.subscribe();
  }

  private attachContent(): void {
    if (!this.overlayRef) return;

    if (this.tooltipContent instanceof TemplateRef) {
      const portal = new TemplatePortal(this.tooltipContent, this.vcr);
      this.overlayRef.attach(portal);
    } else if (typeof this.tooltipContent === 'function') {
      const portal = new ComponentPortal(this.tooltipContent, this.vcr);
      this.overlayRef.attach(portal);
    } else if (typeof this.tooltipContent === 'string') {
      const componentPortal = new ComponentPortal(TooltipStringComponent, this.vcr);
      const ref: ComponentRef<TooltipStringComponent> = this.overlayRef.attach(componentPortal);
      ref.instance.content = this.tooltipContent;
    } else {
      console.warn('[customTooltip] Unsupported tooltipContent type.');
    }
  }

  private listenToTooltipHover(): void {
    if (!this.overlayRef) return;

    const tooltipElement = this.overlayRef.overlayElement;

    tooltipElement.addEventListener('mouseenter', () => {
      this.hoverState = true;
      clearTimeout(this.detachTimeout);
    });

    tooltipElement.addEventListener('mouseleave', () => {
      this.hoverState = false;
      this.onMouseLeave();
    });
  }

  private detach(): void {
    this.overlayRef?.dispose();
    this.overlayRef = undefined;
  }
}
